import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { t } from 'i18next';

import AuthAxios from '../axios';
import { Theme, Track } from '../types';
import { showAppToast } from '../utils/appToast';
import { insertTrackAfterCurrent } from '../utils/playlist/insertTrackAfterCurrent';
import {
  canUserPlayTrack,
  clearResumePlaybackTime,
  clearResumePlayingFlag,
  isAudioReadyToPlay,
  persistResumePlaybackTime,
  persistResumePlayingFlag,
  persistResumeTrackToSession,
  readResumePlaybackTime,
  readResumePlayingFlag,
  readResumeTrackFromSession,
  redirectToMembershipIfNotSubscribed,
  resolveTrackAfterPlaylistFetch,
  safeAudioPlay,
} from '../utils/playlist/musicResumeSession';
import {
  clearPlaylistOrder,
  mergeTracksPreservingOrder,
  orderTracksByIds,
  persistPlaylistOrder,
  playlistFromApiResponse,
  readSavedPlaylistOrder,
  tracksToQueueTrackIds,
} from '../utils/playlist/playlistQueueOrder';
import { getItem, setItem, SYSTEMTHEMESAVEINFO } from '../utils/localStorage';
import { transLn } from '../utils/transLn';
import { AuthStateContext } from './AuthContext';

/** 홈 곡 카드 단일 클릭 전용 — 기본 onChangeMusic 동작과 분리 */
export type OnChangeMusicOptions = {
  insertAfterCurrent?: boolean;
};

type MusicPlayerState = {
  currentPlayMusic: Track | null;
  audioRef: React.MutableRefObject<HTMLAudioElement | null> | null;
  backGroundRef: React.MutableRefObject<HTMLAudioElement | null> | null;
  isMusicPlaying: boolean;
  isThemePlaying: boolean;
  isReady: boolean;
  onChangeMusic: (value: Track, options?: OnChangeMusicOptions) => void;
  onStopMusic: () => void;

  onPause: () => void;
  onPlay: () => void;

  duration: number;
  hidePlayer: () => void;
  showPlayer: () => void;

  volume: number;
  backGroundVolume: number;
  togglePlayPause: () => void;

  handleVolumeChange: (volumeValue: number) => void;
  handleBackGroundVolumeChange: (volumeValue: number) => void;
  handleMuteUnmute: () => void;
  handleBackGroundMuteUnmute: () => void;
  handleBackGroundPlayPause: () => void;
  currentProgress: number;
  buffered: number;

  onChangeCurrentProgressBar: (value: number) => void;
  currentTheme: Theme | null;
  onClickTheme: (theme: Theme) => void;
  // setCurrentTheme: React.Dispatch<React.SetStateAction<Theme | null>>;
  themeList: Theme[];

  playList: Track[];
  setPlayList: React.Dispatch<React.SetStateAction<Track[]>>;
  setCurrentPlayMusic: React.Dispatch<React.SetStateAction<Track | null>>;
  currentSongIndex: number;

  rePlayState: number;
  onClickRePlay: () => void;

  shuffleState: boolean;
  onClickShuffle: () => void;

  onClickLikeBtn: () => Promise<void>;

  onPrev: () => void;
  onNext: () => void;
  onChangePlayListJustAdd: (value: Track[]) => void;
  onChangePlayListOrder: (value: Track[], options?: { silent?: boolean }) => Promise<void>;
  onChangePlayListOrderShuffle: (value: Track[]) => void;
  onChangePlayListAdd: (value: Track[], options?: { silent?: boolean }) => Promise<void>;
  onChangePlayListAddOrderShuffle: (value: Track[]) => void;

  handleSetTimer: (second: number) => void;
  setAlarmTime: React.Dispatch<React.SetStateAction<number>>;
};
export const MusicPlayerStateContext = createContext<MusicPlayerState>({
  currentPlayMusic: null,
  audioRef: null,

  backGroundRef: null,
  isMusicPlaying: false,
  isThemePlaying: false,
  isReady: false,

  duration: 0,
  onChangeMusic: () => {},
  onStopMusic: () => {},

  onPause: () => {},
  onPlay: () => {},

  hidePlayer: () => {},
  showPlayer: () => {},

  togglePlayPause: () => {},

  volume: 0.5,
  backGroundVolume: 0.5,
  handleBackGroundVolumeChange: () => {},
  handleVolumeChange: () => {},
  handleMuteUnmute: () => {},
  handleBackGroundMuteUnmute: () => {},
  handleBackGroundPlayPause: () => {},

  currentProgress: 0,
  buffered: 0,

  onChangeCurrentProgressBar: () => {},

  playList: [],
  currentTheme: null,

  onClickTheme: () => {},

  themeList: [],
  setCurrentPlayMusic: () => {},
  setPlayList: () => {},
  currentSongIndex: 1,

  rePlayState: 0,
  onClickRePlay: () => {},

  shuffleState: false,
  onClickShuffle: () => {},

  onClickLikeBtn: async () => {},

  onPrev: () => {},
  onNext: () => {},
  onChangePlayListOrder: async () => {},
  onChangePlayListOrderShuffle: () => {},
  onChangePlayListAdd: async () => {},
  onChangePlayListAddOrderShuffle: () => {},
  onChangePlayListJustAdd: () => {},
  handleSetTimer: () => {},
  setAlarmTime: () => {},
});
export const MusicContextProvider = ({ children }: { children: React.ReactNode }) => {
  const { isAuthentication, user } = useContext(AuthStateContext);
  const [currentPlayMusic, setCurrentPlayMusic] = useState<Track | null>(() => readResumeTrackFromSession());
  const currentPlayMusicRef = useRef<Track | null>(currentPlayMusic);
  currentPlayMusicRef.current = currentPlayMusic;
  const [themeList, setThemeList] = useState<Theme[]>([]);
  const [currentTheme, setCurrentTheme] = useState<Theme | null>(null);
  const [playList, setPlayList] = useState<Track[]>([]);

  const [isMusicPlaying, setIsMusicPlaying] = useState(false);
  const [isDirection, setIsDirection] = useState(false);
  const [isThemePlaying, setIsThemePlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const backGroundRef = useRef<HTMLAudioElement | null>(null);
  const pendingReloadResumeRef = useRef(readResumePlayingFlag());
  const playlistReadyRef = useRef(!isAuthentication);
  const reloadResumeInFlightRef = useRef(false);
  const shouldAutoPlayRef = useRef(false);
  const isPageUnloadingRef = useRef(false);
  const lastPersistedTimeRef = useRef(0);
  const [currentSongIndex, setCurrentSongIndex] = useState(0);

  const [duration, setDuration] = useState(0);
  const [isReady, setIsReady] = useState(true);
  const [volume, setVolume] = useState(0.5);
  const [currentProgress, setCurrentProgress] = useState(0);
  const [buffered, setBuffered] = useState(0);

  const [backGroundVolume, setBackGroundVolume] = useState(0.5);

  const clearReloadResumeState = () => {
    pendingReloadResumeRef.current = false;
    clearResumePlayingFlag();
    clearResumePlaybackTime();
  };

  const registerGestureResumeRetry = () => {
    const onGesture = () => {
      cleanup();
      attemptReloadResume();
    };
    const opts = { capture: true } as const;
    const cleanup = () => {
      document.removeEventListener('pointerdown', onGesture, opts);
      document.removeEventListener('keydown', onGesture, opts);
    };
    document.addEventListener('pointerdown', onGesture, opts);
    document.addEventListener('keydown', onGesture, opts);
    return cleanup;
  };

  const attemptReloadResume = () => {
    if (!pendingReloadResumeRef.current || reloadResumeInFlightRef.current) return;
    if (isAuthentication && !playlistReadyRef.current) return;

    const track = currentPlayMusicRef.current;
    const audioEl = audioRef.current;
    if (!track || !audioEl || !isAudioReadyToPlay(audioEl)) return;

    if (!canUserPlayTrack(track, isAuthentication, user?.currentSubscription === true)) return;

    reloadResumeInFlightRef.current = true;

    const savedTime = readResumePlaybackTime();
    if (savedTime > 0) {
      try {
        audioEl.currentTime = Math.min(savedTime, Math.max(0, audioEl.duration - 0.25) || savedTime);
      } catch {
        // ignore
      }
    }

    void safeAudioPlay(audioEl).then((played) => {
      reloadResumeInFlightRef.current = false;
      if (played) {
        setIsMusicPlaying(true);
        clearReloadResumeState();
      } else {
        registerGestureResumeRetry();
      }
    });
  };

  /** 음원 반복 재생
   * 0:반복재생 안함.
   * 1:해당 곡만 반복재생.
   * 2:플레이리스트 반복재생.
   */
  const [rePlayState, setRePlayState] = useState<0 | 1 | 2>(2);

  /** 플레이리스트 랜덤 재생하기. */
  const [shuffleState, setShuffleState] = useState<boolean>(false);

  const hasSubscription = user?.currentSubscription === true;

  const blockPlaybackWithoutSubscription = (track?: Track | null) =>
    redirectToMembershipIfNotSubscribed(hasSubscription, track);

  /** Music Play/Pause */
  const togglePlayPause = () => {
    if (isMusicPlaying) {
      audioRef.current?.pause();
      setIsMusicPlaying(false);
      persistResumePlayingFlag(false);
    } else {
      const track = currentPlayMusicRef.current;
      if (!track || !canUserPlayTrack(track, isAuthentication, hasSubscription)) {
        blockPlaybackWithoutSubscription(track);
        return;
      }
      const audio = audioRef.current;
      if (!audio) return;
      void safeAudioPlay(audio).then((played) => {
        setIsMusicPlaying(played);
        if (played) persistResumePlayingFlag(true);
      });
    }
  };

  /** 플레이 리스트에 추가만 하기.*/
  const onChangePlayListJustAdd = (list: Track[]) => {
    if (blockPlaybackWithoutSubscription()) return;
    AuthAxios.post(`/playlist/multiple-tracks`, {
      trackIds: tracksToQueueTrackIds(list),
    })
      .then((resolve) => {
        setPlayList((prev) => {
          // 기존 플레이리스트에 없는 곡만 필터링
          const newTracks = list.filter((newTrack) => !prev.some((prevTrack) => prevTrack.id === newTrack.id));

          // 필터링된 새 곡들을 기존 플레이리스트에 추가
          return [...newTracks, ...prev];
        });
        alert('완료');
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        window.location.reload();
      });
  };

  /**  현재 재생 목록 비우고 곡 추가하기. */
  const onChangePlayListOrder = async (list: Track[], options?: { silent?: boolean }) => {
    if (blockPlaybackWithoutSubscription()) return;
    try {
      await AuthAxios.delete(`/playlist/all`);
      await AuthAxios.post(`/playlist/multiple-tracks`, {
        trackIds: tracksToQueueTrackIds(list),
      });
      const res = await AuthAxios.get(`/playlist`);
      const queue = mergeTracksPreservingOrder(list, playlistFromApiResponse(res.data.list));
      setPlayList(queue);
      persistPlaylistOrder(queue);

      onChangeMusic(list[0]);
      setCurrentSongIndex(0);
      if (!options?.silent) {
        alert('완료');
      }
    } catch (error) {
      console.log(error);
      throw error;
    }
  };

  /**  현재 재생 목록 비우고 곡 추가하고 랜덤. */
  const onChangePlayListOrderShuffle = async (list: Track[]) => {
    if (blockPlaybackWithoutSubscription()) return;
    try {
      await AuthAxios.delete(`/playlist/all`);
      await AuthAxios.post(`/playlist/multiple-tracks`, {
        trackIds: tracksToQueueTrackIds(list),
      });
      const res = await AuthAxios.get(`/playlist`);
      const queue = mergeTracksPreservingOrder(list, playlistFromApiResponse(res.data.list));
      setPlayList(queue);
      persistPlaylistOrder(queue);

      let trackId = Math.floor(Math.random() * (queue.length + 1 - 0 + 1) + 0);

      onClickShuffle();

      onChangeMusic(queue[trackId]);
      alert('완료');
    } catch (error) {
      console.log(error);
    }
  };

  /** 현재 재생 목록에 곡 추가하기. */
  const onChangePlayListAdd = async (list: Track[], options?: { silent?: boolean }) => {
    if (blockPlaybackWithoutSubscription()) return;
    try {
      await AuthAxios.post(`/playlist/multiple-tracks`, {
        trackIds: tracksToQueueTrackIds(list),
      });
      const res = await AuthAxios.get(`/playlist`);
      const queue = playlistFromApiResponse(res.data.list);
      setPlayList(queue);
      persistPlaylistOrder(queue);
      onChangeMusic(queue[0]);
      if (!options?.silent) {
        alert('완료');
      }
    } catch (error) {
      console.log(error);
      throw error;
    }
  };

  /** 현재 재생 목록에 곡 추가하기. 추가되는 재생 목록은 랜덤하게 */
  const onChangePlayListAddOrderShuffle = async (list: Track[]) => {
    if (blockPlaybackWithoutSubscription()) return;
    try {
      const shuffled = list.slice(0).sort(() => Math.random() - 0.5);
      await AuthAxios.post(`/playlist/multiple-tracks`, {
        trackIds: tracksToQueueTrackIds(shuffled),
      });
      const res = await AuthAxios.get(`/playlist`);
      const queue = playlistFromApiResponse(res.data.list);
      setPlayList(queue);
      onChangeMusic(queue[0]);
      alert('완료');
    } catch (error) {
      console.log(error);
    }
  };

  /** 한 곡 반복 — 트랙이 자연 종료된 뒤 동일 곡 0초부터 재생 */
  const restartCurrentTrack = () => {
    if (blockPlaybackWithoutSubscription(currentPlayMusicRef.current)) return;
    if (!currentPlayMusic) return;

    const audio = audioRef.current;
    if (!audio) return;

    audio.currentTime = 0;
    setCurrentProgress(0);
    lastPersistedTimeRef.current = 0;
    persistResumePlaybackTime(0);

    void safeAudioPlay(audio).then((played) => {
      setIsMusicPlaying(played);
      if (played) persistResumePlayingFlag(true);
    });
  };

  /** 다음 곡플레이 — 한 곡 반복(rePlayState===1)일 때도 목록에서 다음 곡으로 이동 (끝까지 재생 시 반복은 onEnded) */
  const onNext = () => {
    if (blockPlaybackWithoutSubscription(currentPlayMusicRef.current)) return;
    setIsDirection(true);
    //셔플함===랜덤재생함.
    if (shuffleState) {
      //랜덤한 인덱스 위치 추출하기.
      let randomIndex = Math.floor(Math.random() * playList.length);
      let currentIndex = playList.findIndex((item) => item.id === currentPlayMusic?.id);

      if (rePlayState === 0) {
        // 반복재생 안함.
        if (playList[currentSongIndex + 1]) {
          setCurrentPlayMusic(playList[randomIndex]);
          setCurrentSongIndex((prev) => prev + 1);
        }
      } else {
        // 한 곡 반복·전체 반복 — 버튼은 큐에서 다음(랜덤)으로
        setCurrentPlayMusic(playList[randomIndex]);
      }
    }
    // 셔플 안함===랜덤재생 안함.
    else {
      let currentIndex = playList.findIndex((item) => item.id === currentPlayMusic?.id);

      if (rePlayState === 0) {
        if (playList.length - 1 === currentIndex) {
          setCurrentSongIndex(0);
          setCurrentPlayMusic(playList[0]);
        } else if (currentIndex === 0) {
          setCurrentSongIndex(1);
          setCurrentPlayMusic(playList[1]);
        } else {
          setCurrentPlayMusic(playList[currentSongIndex + 1]);
          setCurrentSongIndex((prev) => prev + 1);
          if (currentSongIndex === playList.length || currentSongIndex === playList.length + 1) {
            setCurrentPlayMusic(playList[playList.length - 3]);
          }
        }
      } else {
        // 한 곡 반복·전체 반복 — 버튼은 큐에서 다음으로 (마지막이면 처음)
        if (playList.length - 1 === currentIndex) {
          setCurrentSongIndex(0);
          setCurrentPlayMusic(playList[0]);
        } else {
          setCurrentPlayMusic(playList[currentIndex + 1]);
          setCurrentSongIndex(currentIndex + 1);
        }
      }
    }
  };
  /** 이전 곡 플레이 — 큐 첫 곡이면 이전 트랙 대신 해당 곡 처음부터 재생 */
  const onPrev = () => {
    if (blockPlaybackWithoutSubscription(currentPlayMusicRef.current)) return;
    if (!currentPlayMusic || playList.length === 0) return;

    const currentIndex = playList.findIndex((item) => item.id === currentPlayMusic.id);
    if (currentIndex <= 0) {
      restartCurrentTrack();
      return;
    }

    setIsDirection(false);

    if (shuffleState) {
      const randomIndex = Math.floor(Math.random() * playList.length);
      if (rePlayState === 0 && !playList[currentSongIndex - 1]) {
        return;
      }
      setCurrentPlayMusic(playList[randomIndex]);
      if (rePlayState === 0) {
        setCurrentSongIndex((prev) => prev - 1);
      }
    } else if (rePlayState === 0) {
      if (currentIndex === playList.length - 1) {
        setCurrentPlayMusic(playList[playList.length - 2]);
        setCurrentSongIndex(playList.length - 2);
      } else {
        setCurrentPlayMusic(playList[currentSongIndex - 1]);
        setCurrentSongIndex((prev) => prev - 1);
      }
    } else {
      setCurrentPlayMusic(playList[currentIndex - 1]);
      setCurrentSongIndex(currentIndex - 1);
    }
  };

  /** 현재 곡이 끝나면.. */
  const onEnded = () => {
    // 만약 현재 rePlayState===0이면 반복 재생 안함. 해당 곡 끝나면 멈춤. 다음곡 버튼을 눌러야 다음곡 재생함.
    if (rePlayState === 0) {
      return;
    }
    // 만약 현재 rePlayState===1이면 현재 곡만 무한반복 (끝까지 재생 시 처음부터).
    else if (rePlayState === 1) {
      restartCurrentTrack();
    }
    // 만약 현재 rePlayState===2이면 플레이리스트 무한반복.
    else if (rePlayState === 2) {
      onNext();
    }
  };

  /** 볼륨 조절 */
  const handleVolumeChange = (volumeValue: number) => {
    if (!audioRef.current) return;
    audioRef.current.volume = volumeValue;
    setVolume(volumeValue);
  };

  /** 배경음악 선택
   * 배경음악을 선택하면 정보를 로컬에 저장하고 저장된 정보를 나중에 불러오기 위해 사용함.
   */
  const onClickTheme = (theme: Theme) => {
    setItem(SYSTEMTHEMESAVEINFO, theme.id);
    setCurrentTheme(theme);
  };

  /** 배경음 볼륨 조절 */
  const handleBackGroundVolumeChange = (volumeValue: number) => {
    if (!backGroundRef.current) return;
    backGroundRef.current.volume = volumeValue;
    setBackGroundVolume(volumeValue);
  };

  /** 뮤트 ON/OFF */
  const handleMuteUnmute = () => {
    if (!audioRef.current) return;
    if (audioRef.current.volume !== 0) {
      audioRef.current.volume = 0;
      // setVolume(0);
    } else {
      audioRef.current.volume = 1;
      // setVolume(1);
    }
  };
  /** 배경음악 볼륨 조절 */
  const handleBackGroundMuteUnmute = () => {
    if (backGroundRef.current?.volume !== 0) {
      backGroundRef.current!.volume = 0;
      // setVolume(0);
    } else {
      backGroundRef.current.volume = 1;
      // setVolume(1);
    }
  };
  /** 배경음악 ON/OFF */
  const handleBackGroundPlayPause = () => {
    if (isThemePlaying) {
      onThemePause();
    } else {
      onThemePlay();
    }
  };

  /** 좋아요 버튼 클릭 — 낙관적 반영 후 실패 시 롤백 */
  const onClickLikeBtn = (): Promise<void> => {
    if (!currentPlayMusic) return Promise.resolve();

    const trackId = currentPlayMusic.id;
    const liked = !!currentPlayMusic.isLiked;
    const nextLiked = !liked;

    const syncLiked = (next: boolean) => {
      setCurrentPlayMusic((prev) => (prev ? { ...prev, isLiked: next } : prev));
      setPlayList((prevList) =>
        prevList.map((item) => (item.id === trackId ? { ...item, isLiked: next } : item)),
      );
    };

    syncLiked(nextLiked);

    const request = liked
      ? AuthAxios.post(`/like/delete/multiple`, { trackIds: [trackId] })
      : AuthAxios.post(`/like`, { trackId });

    return request
      .then(() => undefined)
      .catch((error) => {
        console.log(error);
        syncLiked(liked);
        throw error;
      });
  };

  const onChangeCurrentProgressBar = (currentValue: number) => {
    setCurrentProgress(currentValue);
  };

  const handleBufferProgress: React.ReactEventHandler<HTMLAudioElement> = (e) => {
    const audio = e.currentTarget;
    const dur = audio.duration;
    if (dur > 0) {
      for (let i = 0; i < audio.buffered.length; i++) {
        if (audio.buffered.start(audio.buffered.length - 1 - i) < audio.currentTime) {
          const bufferedLength = audio.buffered.end(audio.buffered.length - 1 - i);
          setBuffered(bufferedLength);
          break;
        }
      }
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(Math.floor(audioRef.current.duration));
    }
  };

  /** 사운드 클릭 */
  const onChangeMusic = async (data: Track, options?: OnChangeMusicOptions) => {
    if (!canUserPlayTrack(data, isAuthentication, hasSubscription)) {
      blockPlaybackWithoutSubscription(data);
      return;
    }

    try {
      const previousCurrentId = currentPlayMusic?.id;
      const insertAfterCurrent = options?.insertAfterCurrent === true;

      const res = await AuthAxios.post('/record', {
        trackId: data.id,
      });
      const trackToPlay: Track = res.data;
      const addToPlayList = true;

      let playlistUpdate: ReturnType<typeof insertTrackAfterCurrent> | null = null;

      if (addToPlayList && insertAfterCurrent) {
        playlistUpdate = insertTrackAfterCurrent(playList, trackToPlay, previousCurrentId);
        setPlayList(playlistUpdate.nextList);
        setCurrentSongIndex(playlistUpdate.nextIndex);

        AuthAxios.delete('/playlist/all')
          .then(() =>
            AuthAxios.post('/playlist/multiple-tracks', {
              trackIds: tracksToQueueTrackIds(playlistUpdate!.nextList),
            }),
          )
          .catch((error) => {
            console.log(error);
          });

        if (playlistUpdate.wasNewlyAdded) {
          showAppToast(t('homepage.toastAddedToCurrentPlaylist'));
        }
      }

      if (addToPlayList && insertAfterCurrent && playlistUpdate) {
        setIsDirection(true);
      }

      setCurrentPlayMusic(trackToPlay);

      if (addToPlayList && !insertAfterCurrent) {
        AuthAxios.post(`/playlist`, {
          trackId: data.id,
        })
          .then(() => {
            setPlayList((prev) => {
              const isTrackExists = prev.some((track: Track) => track.id === data.id);
              if (!isTrackExists) {
                return [data, ...prev];
              }
              return prev;
            });
          })
          .catch((error) => {
            console.log(error);
          });
      }
    } catch (error) {
      console.log(error);
    }
  };

  const onPause = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsMusicPlaying(false);
      persistResumePlayingFlag(false);
    }
  };
  const onPlay = () => {
    const track = currentPlayMusicRef.current;
    if (!track || !canUserPlayTrack(track, isAuthentication, hasSubscription)) {
      onPause();
      return;
    }

    let currentIndex = playList.findIndex((item) => item.id === currentPlayMusic?.id);
    if (currentSongIndex === playList.length) setCurrentSongIndex(playList.length - 1);
    // onprev
    if (!isDirection) {
      if (playList.length - 1 === currentIndex) {
        setCurrentSongIndex(1);

        if (currentSongIndex === playList.length - 1 || currentSongIndex === playList.length) {
          setCurrentSongIndex(1);
          if (currentSongIndex === playList.length + 1) {
            setCurrentSongIndex(1);
          }
        }
      } else if (currentIndex === 0) {
        if (currentSongIndex === -1) setCurrentSongIndex(0);
        setCurrentSongIndex(currentIndex + 1);
      } else {
        setCurrentSongIndex(currentIndex);
      }
    } else {
      if (playList.length - 1 === currentIndex) {
        setCurrentSongIndex(1);
        if (currentSongIndex === playList.length + 1) {
          setCurrentSongIndex(1);
        }
      } else if (currentIndex === 0) {
        if (currentSongIndex === -1) setCurrentSongIndex(0);
        setCurrentSongIndex(1);
      } else {
        setCurrentSongIndex(currentIndex);
      }
    }
    if (audioRef.current) {
      void safeAudioPlay(audioRef.current).then((played) => {
        setIsMusicPlaying(played);
      });
    }
  };

  const onThemePause = () => {
    if (backGroundRef.current) {
      backGroundRef.current.pause();
      setIsThemePlaying(false);
    }
  };
  const onThemePlay = () => {
    if (backGroundRef.current) {
      const playPromise = backGroundRef.current.play();
      if (playPromise !== undefined) {
        playPromise
          .then((_) => {
            // Automatic playback started!
            // Show playing UI.
          })
          .catch((error) => {
            // Auto-play was prevented
            // Show paused UI.
          });
      }
      setIsThemePlaying(true);
    }
  };

  const onStopMusic = () => {
    setIsMusicPlaying(false); // Hide the player
    clearReloadResumeState();
    persistResumeTrackToSession(null);

    if (audioRef.current !== null) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;

      audioRef.current.oncanplaythrough = null; // remove the event listener
    }
    setTimeout(() => {
      setCurrentPlayMusic(null);
    }, 600);
  };

  //음원 플레이어 visible
  const showPlayer = () => {
    setIsMusicPlaying(true);
  };

  //음원 플레이어 hidden
  const hidePlayer = () => {
    setIsMusicPlaying(false);
  };

  const [alarmTime, setAlarmTime] = useState(0);
  const [timerActive, setTimerActive] = useState(false);
  const handleSetTimer = (second: number) => {
    setAlarmTime(second);
    setTimerActive(true);
  };

  /** 반복 재생 컨트롤러 */
  const onClickRePlay = () => {
    switch (rePlayState) {
      case 0:
        setRePlayState(1);
        break;
      case 1:
        setRePlayState(2);
        if (playList.length - 1 === currentSongIndex) {
          setCurrentSongIndex(1);
        }

        break;
      case 2:
        setRePlayState(0);
        break;
    }
  };
  /** 곡 셔플 컨트롤러 */
  const onClickShuffle = () => {
    setShuffleState((prev) => !prev);
  };

  const value = {
    currentPlayMusic,
    isMusicPlaying,
    isThemePlaying,
    audioRef,
    backGroundRef,

    onChangeMusic,
    onStopMusic,

    onPause,
    onPlay,

    duration,
    isReady,
    showPlayer,
    hidePlayer,

    togglePlayPause,

    volume,
    backGroundVolume,
    handleVolumeChange,
    handleBackGroundVolumeChange,
    handleMuteUnmute,
    handleBackGroundMuteUnmute,
    handleBackGroundPlayPause,

    currentProgress,
    buffered,
    onChangeCurrentProgressBar,

    currentTheme,

    onClickTheme,
    themeList,
    playList,
    setPlayList,
    setCurrentPlayMusic,
    currentSongIndex,

    rePlayState,
    onClickRePlay,

    shuffleState,
    onClickShuffle,
    onClickLikeBtn,

    onPrev,
    onNext,
    onChangePlayListJustAdd,
    onChangePlayListOrder,
    onChangePlayListOrderShuffle,
    onChangePlayListAdd,
    onChangePlayListAddOrderShuffle,
    handleSetTimer,
    setAlarmTime,
  };
  /** 배경 테마 가져오기. */
  useEffect(() => {
    AuthAxios.get(`/theme/list?page=1&perPage=30`)
      .then((resolve) => {
        setThemeList(resolve.data.list);
        let currentThemeId = getItem(SYSTEMTHEMESAVEINFO);
        if (resolve.data.list.some((item: Theme) => item.id === currentThemeId) && currentThemeId) {
          let idx = resolve.data.list.findIndex((item: Theme) => item.id === currentThemeId);
          setCurrentTheme(resolve.data.list[idx]);
        } else {
          setCurrentTheme(resolve.data.list[0]);
        }
      })

      .catch((error) => {
        console.log(error);
      });
  }, []);

  /** 곡 변경 시 src 로드 후 onCanPlay에서 한 번만 재생 (play/pause 경쟁 방지) */
  useEffect(() => {
    if (!currentPlayMusic) {
      shouldAutoPlayRef.current = false;
      return;
    }
    if (pendingReloadResumeRef.current) return;
    if (!canUserPlayTrack(currentPlayMusic, isAuthentication, hasSubscription)) {
      shouldAutoPlayRef.current = false;
      return;
    }
    shouldAutoPlayRef.current = true;
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsMusicPlaying(false);
    }
  }, [currentPlayMusic?.id, isAuthentication, hasSubscription]);

  useEffect(() => {
    persistResumeTrackToSession(currentPlayMusic);
  }, [currentPlayMusic]);

  useEffect(() => {
    if (isMusicPlaying) {
      persistResumePlayingFlag(true);
    }
  }, [isMusicPlaying]);

  useEffect(() => {
    playlistReadyRef.current = !isAuthentication;
  }, [isAuthentication]);

  useEffect(() => {
    const saveBeforeUnload = () => {
      isPageUnloadingRef.current = true;
      if (isMusicPlaying) {
        persistResumePlayingFlag(true);
      }
      persistResumeTrackToSession(currentPlayMusic);
      persistPlaylistOrder(playList);
      if (audioRef.current) {
        persistResumePlaybackTime(audioRef.current.currentTime);
      }
    };
    window.addEventListener('beforeunload', saveBeforeUnload);
    window.addEventListener('pagehide', saveBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', saveBeforeUnload);
      window.removeEventListener('pagehide', saveBeforeUnload);
    };
  }, [isMusicPlaying, currentPlayMusic, playList]);
  useEffect(() => {
    if (currentTheme) {
      onThemePlay();
    }
  }, [currentTheme?.id]);

  useEffect(() => {
    let interval: any;
    if (timerActive && alarmTime > 0) {
      interval = setInterval(() => {
        setAlarmTime((prev) => prev - 1);
      }, 1000);
    } else if (alarmTime === 0 && timerActive) {
      setTimerActive(false);

      onPause();
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [timerActive, alarmTime]);

  useEffect(() => {
    if (playList.length > 0) {
      persistPlaylistOrder(playList);
    }
  }, [playList]);

  useEffect(() => {
    if (!isAuthentication) {
      setPlayList([]);
      clearPlaylistOrder();
      playlistReadyRef.current = true;
      if (readResumePlayingFlag()) {
        pendingReloadResumeRef.current = true;
        queueMicrotask(() => attemptReloadResume());
      }
      return;
    }

    let cancelled = false;
    playlistReadyRef.current = false;

    AuthAxios.get('/playlist')
      .then((resolve) => {
        if (cancelled) return;

        const apiList = playlistFromApiResponse(resolve?.data?.list);
        const savedOrder = readSavedPlaylistOrder();
        const fetchedList =
          savedOrder.length > 0 ? orderTracksByIds(apiList, savedOrder) : apiList;
        setPlayList(fetchedList);

        if (fetchedList.length > 0 && hasSubscription) {
          const resumeTrack = readResumeTrackFromSession();
          const { track: nextTrack, index: nextIndex } = resolveTrackAfterPlaylistFetch(
            fetchedList,
            resumeTrack,
            currentPlayMusicRef.current,
          );
          setCurrentPlayMusic(nextTrack);
          setCurrentSongIndex(nextIndex);
          persistResumeTrackToSession(nextTrack);
        }

        if (readResumePlayingFlag() && hasSubscription) {
          pendingReloadResumeRef.current = true;
        }
      })
      .catch((error) => {
        console.error('Failed to fetch playlist:', error);
      })
      .finally(() => {
        if (!cancelled) {
          playlistReadyRef.current = true;
          if (pendingReloadResumeRef.current) {
            queueMicrotask(() => attemptReloadResume());
          }
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthentication, hasSubscription]);

  useEffect(() => {
    if (hasSubscription) return;
    pendingReloadResumeRef.current = false;
    clearReloadResumeState();
    const track = currentPlayMusicRef.current;
    if (track && !track.isFree) {
      shouldAutoPlayRef.current = false;
      onPause();
      setCurrentPlayMusic(null);
      persistResumeTrackToSession(null);
    }
  }, [hasSubscription]);

  useEffect(() => {
    const handlePlay = () => {
      if (backGroundRef.current) {
        backGroundRef.current.pause();
      }
    };

    const handleEnded = () => {
      if (backGroundRef.current) {
        backGroundRef.current.play();
      }
    };

    if (audioRef?.current) {
      audioRef.current.addEventListener('play', handlePlay);
      audioRef.current.addEventListener('ended', handleEnded);
      audioRef.current.addEventListener('pause', handleEnded);
    }

    // 컴포넌트가 언마운트 될 때 이벤트 리스너를 제거합니다.
    return () => {
      if (audioRef?.current) {
        audioRef.current.removeEventListener('play', handlePlay);
        audioRef.current.removeEventListener('ended', handleEnded);
        audioRef.current.removeEventListener('pause', handleEnded); // Here, you should use 'removeEventListener' instead of 'addEventListener'
      }
    };
  }, []);

  return (
    <MusicPlayerStateContext.Provider value={value}>
      {/* 배경 사운드 */}
      <audio
        loop={true}
        ref={backGroundRef}
        src={currentTheme?.url && `${process.env.REACT_APP_IMG_ENDPOINT}/${currentTheme.url}`}
        onCanPlay={(e) => {
          e.currentTarget.volume = 0.0;
        }}
        onVolumeChange={(e) => handleBackGroundVolumeChange(e.currentTarget.volume)}
        onPlay={(e) => {
          setIsThemePlaying(true);
        }}
        onPause={(e) => {
          setIsThemePlaying(false);
        }}
      >
        <source
          src={currentTheme?.url && `${process.env.REACT_APP_IMG_ENDPOINT}/${currentTheme.url}`}
          type="audio/mpeg"
        />
      </audio>
      {/* 사용자 플레이 사운드 */}
      <audio
        ref={audioRef}
        src={currentPlayMusic?.url && `${process.env.REACT_APP_IMG_ENDPOINT}/${currentPlayMusic.url}`}
        onLoadedMetadata={handleLoadedMetadata}
        onDurationChange={(e) => setDuration(e.currentTarget.duration)}
        onCanPlay={(e) => {
          const audio = e.currentTarget;
          audio.volume = volume;
          setIsReady(true);

          const track = currentPlayMusicRef.current;
          if (
            shouldAutoPlayRef.current &&
            track &&
            canUserPlayTrack(track, isAuthentication, hasSubscription)
          ) {
            shouldAutoPlayRef.current = false;
            void safeAudioPlay(audio).then((played) => setIsMusicPlaying(played));
          }

          attemptReloadResume();
        }}
        onTimeUpdate={(e) => {
          setCurrentProgress(e.currentTarget.currentTime);
          handleBufferProgress(e);
          if (isMusicPlaying && !isPageUnloadingRef.current) {
            const t = e.currentTarget.currentTime;
            if (t - lastPersistedTimeRef.current >= 2) {
              lastPersistedTimeRef.current = t;
              persistResumePlaybackTime(t);
            }
          }
        }}
        onPlay={() => {
          setIsMusicPlaying(true);
        }}
        onPause={() => {
          setIsMusicPlaying(false);
        }}
        onEnded={() => {
          // onNext();
          onEnded();
        }}
        onProgress={handleBufferProgress}
        onVolumeChange={(e) => setVolume(e.currentTarget.volume)}
      >
        <source
          src={currentPlayMusic?.url && `${process.env.REACT_APP_IMG_ENDPOINT}/${currentPlayMusic.url}`}
          type="audio/mpeg"
        />
        <source
          src={currentPlayMusic?.url && `${process.env.REACT_APP_IMG_ENDPOINT}/${currentPlayMusic.url}`}
          type="audio/ogg"
        />
        <source
          src={currentPlayMusic?.url && `${process.env.REACT_APP_IMG_ENDPOINT}/${currentPlayMusic.url}`}
          type="audio/mp3"
        />
        <source
          src={currentPlayMusic?.url && `${process.env.REACT_APP_IMG_ENDPOINT}/${currentPlayMusic.url}`}
          type="audio/wav"
        />
        <source
          src={currentPlayMusic?.url && `${process.env.REACT_APP_IMG_ENDPOINT}/${currentPlayMusic.url}`}
          type="audio/mp4"
        />
        <source
          src={currentPlayMusic?.url && `${process.env.REACT_APP_IMG_ENDPOINT}/${currentPlayMusic.url}`}
          type="audio/oga"
        />
        <meta itemProp="image" content={currentPlayMusic?.thumbnail} />
        <meta itemProp="name" content={currentPlayMusic?.title ? transLn(currentPlayMusic.title) : ''} />
        <meta itemProp="artist" content="soundPill" />
      </audio>
      {children}
    </MusicPlayerStateContext.Provider>
  );
};
